import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { X } from "lucide-react";
import { AppConfigKey, AppConfigKeyType } from "@/services/appConfigApi";

interface AppConfigKeyRowProps {
  row: AppConfigKey;
  onChange: (next: AppConfigKey) => void;
  onRemove: () => void;
  invalidKey?: boolean;
  invalidValue?: boolean;
}

const TYPE_OPTIONS: { value: AppConfigKeyType; label: string }[] = [
  { value: "string", label: "String" },
  { value: "integer", label: "Integer" },
  { value: "boolean", label: "Boolean" },
  { value: "array", label: "Array" },
];

/**
 * Compact Key | Type | Value | ✕ row used inside the App Configuration
 * dialog. Type-aware value control:
 *   - boolean → Switch (true/false)
 *   - integer → Input type="number"
 *   - array   → Input with comma-separator hint
 *   - string  → Input
 */
export const AppConfigKeyRow = ({
  row,
  onChange,
  onRemove,
  invalidKey,
  invalidValue,
}: AppConfigKeyRowProps) => {
  const handleTypeChange = (nextType: string) => {
    const t = nextType as AppConfigKeyType;
    let nextValue = row.value;
    // Reset value when switching to a type whose existing string isn't valid
    // for the new type (avoids surprising coercion errors at save time).
    if (t === "boolean" && row.value !== "true" && row.value !== "false") {
      nextValue = "false";
    }
    if (t === "integer" && row.value && Number.isNaN(Number(row.value))) {
      nextValue = "";
    }
    onChange({ ...row, type: t, value: nextValue });
  };

  return (
    <div className="grid grid-cols-[minmax(0,1fr)_120px_minmax(0,1.4fr)_auto] items-start gap-2">
      <div>
        <Input
          value={row.key}
          onChange={(e) => onChange({ ...row, key: e.target.value })}
          placeholder="key_name"
          aria-label="Configuration key"
          aria-invalid={invalidKey || undefined}
          className={invalidKey ? "border-destructive" : undefined}
        />
      </div>
      <Select value={row.type} onValueChange={handleTypeChange}>
        <SelectTrigger aria-label="Value type">
          <SelectValue />
        </SelectTrigger>
        <SelectContent>
          {TYPE_OPTIONS.map((opt) => (
            <SelectItem key={opt.value} value={opt.value}>
              {opt.label}
            </SelectItem>
          ))}
        </SelectContent>
      </Select>
      <div>
        {row.type === "boolean" ? (
          <div className="flex h-10 items-center gap-3 rounded-md border border-input bg-background px-3">
            <Switch
              checked={row.value === "true"}
              onCheckedChange={(checked) =>
                onChange({ ...row, value: checked ? "true" : "false" })
              }
              aria-label="Boolean value"
            />
            <span className="text-sm text-muted-foreground">
              {row.value === "true" ? "True" : "False"}
            </span>
          </div>
        ) : (
          <Input
            value={row.value}
            onChange={(e) => onChange({ ...row, value: e.target.value })}
            placeholder={
              row.type === "integer"
                ? "e.g. 42"
                : row.type === "array"
                  ? "value1, value2, value3"
                  : "value"
            }
            inputMode={row.type === "integer" ? "numeric" : undefined}
            aria-label="Configuration value"
            aria-invalid={invalidValue || undefined}
            className={invalidValue ? "border-destructive" : undefined}
          />
        )}
      </div>
      <Button
        type="button"
        variant="ghost"
        size="icon"
        onClick={onRemove}
        aria-label="Remove key"
        className="h-10 w-10 shrink-0"
      >
        <X className="h-4 w-4" />
      </Button>
    </div>
  );
};
